import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { take } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import {
  DeletePositionDialogData,
  DeletePositionDialogResult,
  TellentPosition,
} from '@core/tellent-positions-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  IconComponent,
  NotificationsController,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tlc-delete-position-dialog',
  templateUrl: './delete-position-dialog.component.html',
  styleUrls: ['./delete-position-dialog.component.less'],
  imports: [
    DialogBodyComponent,
    TranslatePipe,
    ButtonComponent,
    DialogHeaderComponent,
    DialogFooterComponent,
    ReactiveFormsModule,
    ButtonLoadingDirective,
    IconComponent,
    CheckboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeletePositionDialogComponent implements DialogComponentI<
  DeletePositionDialogData,
  DeletePositionDialogResult
> {
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);
  private readonly dialogRef = inject(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly notifications = inject(NotificationsController);

  public loading = signal<boolean>(false);
  public positionData = signal<TellentPosition | null>(null);
  public confirmed = signal<boolean>(false);

  public setDialogData(data: DeletePositionDialogData, triggerUpdate: boolean): void {
    this.positionData.set(data.position);
  }

  public onConfirmedChange(value: string | boolean): void {
    this.confirmed.set(value !== false);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public delete(): void {
    this.loading.set(true);
    this.positionsDataFacade
      .deletePosition(this.positionData()!.guid)
      .pipe(take(1))
      .subscribe(result => {
        this.loading.set(false);
        if (result.isSuccess) {
          this.notifications.success(
            this.i18n.translate('tlc.positions.delete_position_dialog.notifications.success'),
          );
          this.dialogRef.close({ success: true });
        } else {
          this.notifications.error(
            this.i18n.translate('tlc.positions.delete_position_dialog.notifications.error'),
          );
        }
      });
  }
}
